"use client";

import { useSyncExternalStore } from "react";

// Installing Sweet as an app (components/pwa/Install.tsx): the browser's own install prompt (Chrome, Edge,
// Samsung Internet on Android and the desktop), iPhone/iPad, where it's done by hand from the Share menu,
// the visitor closing the banner, and a lasting "installed" flag so the offer never comes back once they
// have the app.

const INSTALLED_KEY = "sweet.pwa.installed";
const DISMISSED_KEY = "sweet.pwa.dismissed";
/** How long the closed banner stays away. */
const DISMISS_DAYS = 14;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface PwaState {
  /** The browser offers its install dialog. */
  canPrompt: boolean;
  /** Running as the installed app. */
  standalone: boolean;
  /** iPhone or iPad: installed by hand, from the Share menu. */
  ios: boolean;
  /** The visitor has the app (installed here, opened it from the home screen, or the browser says so). */
  installed: boolean;
}

const SERVER: PwaState = { canPrompt: false, standalone: false, ios: false, installed: false };
let state: PwaState = SERVER;
let deferred: BeforeInstallPromptEvent | null = null;
let started = false;
const listeners = new Set<() => void>();

function set(next: Partial<PwaState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function start() {
  if (started || typeof window === "undefined") return;
  started = true;
  const nav = navigator as Navigator & { standalone?: boolean };
  // iPadOS says it's a Mac: a Mac with a touch screen is an iPad.
  const ios = /iphone|ipad|ipod/i.test(nav.userAgent) || (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  const media = window.matchMedia("(display-mode: standalone)");
  // The prompt can come before React hydrates: the script in app/layout.tsx keeps it.
  const early = (window as Window & { __pwaInstallPrompt?: BeforeInstallPromptEvent }).__pwaInstallPrompt;
  if (early) deferred = early;
  const standalone = media.matches || nav.standalone === true;
  // Opened from the home screen: it's installed. On Android the app and the browser share storage, so the
  // browser learns it too; on iPhone the app keeps its own (see markInstalled).
  if (standalone) remember();
  state = { ...state, ios, standalone, canPrompt: Boolean(early), installed: standalone || installedBefore() };
  media.addEventListener("change", (e) => set({ standalone: e.matches }));
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // our own button instead of the browser's bar
    deferred = e as BeforeInstallPromptEvent;
    set({ canPrompt: true });
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    markInstalled();
    set({ canPrompt: false });
  });
  // Chrome on Android tells a tab that this site's app is installed (the manifest's related_applications).
  const related = (navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> }).getInstalledRelatedApps;
  related
    ?.call(navigator)
    .then((apps) => {
      if (apps.length) markInstalled();
    })
    .catch(() => {});
}

function remember() {
  try {
    window.localStorage.setItem(INSTALLED_KEY, "1");
  } catch {
    // Storage blocked: the banner may come back, and can be closed.
  }
}

function installedBefore(): boolean {
  try {
    return window.localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
}

/** The visitor installed the app (on iPhone: went through the Add to Home Screen steps): no more banner. */
export function markInstalled() {
  remember();
  set({ installed: true });
}

export function usePwa(): PwaState {
  return useSyncExternalStore(
    (cb) => {
      start();
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => {
      start();
      return state;
    },
    () => SERVER,
  );
}

/** Opens the browser's install dialog; true when the visitor installs. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const event = deferred;
  deferred = null; // it works once
  set({ canPrompt: false });
  await event.prompt();
  const { outcome } = await event.userChoice;
  if (outcome === "accepted") markInstalled();
  return outcome === "accepted";
}

export function installDismissed(): boolean {
  try {
    const at = Number(window.localStorage.getItem(DISMISSED_KEY));
    return Boolean(at) && Date.now() - at < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

export function dismissInstall() {
  try {
    window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    // Storage blocked: the banner comes back next visit.
  }
}

